import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../../components/layout';
import {
  Alert,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Pagination,
  StatusBadge,
  useNow,
} from '../../components/core';
import { useUiStore } from '../../store/ui';
import { useBrandingStore } from '../../store/branding';
import { http } from '../../lib/api';
import { loadCheckout } from '../../lib/razorpay';
import { useHasActivePlan } from '../../hooks';
import type {
  BillingCycle,
  Paginated,
  PlanDto,
  RazorpayOrderResponseDto,
  StreamDto,
} from '@livestreamx/shared-types';
import { priceForCycle, PLAN_STATE_LABEL } from '@livestreamx/shared-types';
import { NAV } from './nav';

/* Compute a human-readable end-time / status string. */
function formatEndInfo(s: StreamDto): string {
  if (s.endedAt) {
    const ago = Math.floor((Date.now() - new Date(s.endedAt).getTime()) / 1000);
    if (ago < 60) return 'Ended moments ago';
    if (ago < 3600) return `Ended ${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `Ended ${Math.floor(ago / 3600)}h ago`;
    return `Ended ${Math.floor(ago / 86400)}d ago`;
  }
  if (s.status === 'LIVE' && s.startedAt) {
    const elapsed = Math.floor((Date.now() - new Date(s.startedAt).getTime()) / 1000);
    const d = Math.floor(elapsed / 86400);
    const h = Math.floor((elapsed % 86400) / 3600);
    const m = Math.floor((elapsed % 3600) / 60);
    return d > 0 ? `${d}d ${h}h ${m}m live` : `${h}h ${m}m live`;
  }
  return 'Not started';
}

/**
 * Renewal date shown on the card. Falls back to "Not set" when the stream has
 * never been renewed — the plan-picker popup then picks one on first purchase.
 */
function formatRenewDate(s: StreamDto): string {
  if (!s.renewsAt) return 'Not set';
  const d = new Date(s.renewsAt);
  if (Number.isNaN(d.getTime())) return 'Not set';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const longDate = (d: string) =>
  new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

/** Slice of GET /api/billing/subscription the plan strip renders. */
interface SubPlanRow {
  id: string;
  planId: string;
  name: string;
  streamsUsed: number;
  remainingStreams: number;
  endAt: string | null;
}

interface SubView {
  status: string;
  billingCycle: string | null;
  endAt: string | null;
  /** Entitled (ACTIVE + unexpired) plans — empty once the due date has lapsed. */
  plans: SubPlanRow[];
}

/** Inline WhatsApp glyph for the "contact administrator" link. */
function WhatsAppIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="currentColor"
      aria-hidden="true"
      style={{ verticalAlign: '-2px' }}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

function StreamCard({
  stream,
  onDelete,
  onRenew,
}: {
  stream: StreamDto;
  onDelete: (s: StreamDto) => void;
  onRenew: (s: StreamDto) => void;
}) {
  // `/streams/<slug>`; slug is always present from the API, but never emit a
  // broken `/streams/undefined` href if a stale payload lacks it.
  const detailHref = `/streams/${stream.slug || stream.id}`;
  // Only `ACTIVE` may publish. A lapsed stream still renders — with its URLs and
  // key intact — behind a chip naming the reason, so the tenant renews instead
  // of wondering where the stream went.
  const blocked = stream.planState && stream.planState !== 'ACTIVE' ? stream.planState : null;
  return (
    <div className="stream-card card">
      <div className="stream-card-header">
        <Link to={detailHref} className="stream-card-title">{stream.name}</Link>
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          {blocked ? (
            <StatusBadge
              status={blocked === 'DEACTIVATED' ? 'SUSPENDED' : 'EXPIRED'}
              label={PLAN_STATE_LABEL[blocked]}
            />
          ) : null}
          <StatusBadge status={stream.status} />
        </div>
      </div>
      <Link to={detailHref} className="stream-card-thumb">
        {stream.thumbnailUrl ? (
          <img src={stream.thumbnailUrl} alt={stream.name} />
        ) : (
          <div className="stream-card-thumb-placeholder">
            <span className="stream-card-play-icon">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </div>
        )}
      </Link>
      <div className="stream-card-meta">
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">Stream name</span>
          <span className="text-sm font-medium">{stream.name}</span>
        </div>
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">Plan</span>
          <span className="text-sm font-medium">
            {stream.plan?.name ?? PLAN_STATE_LABEL[stream.planState ?? 'NO_PLAN']}
          </span>
        </div>
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">Renew date</span>
          <span className="text-sm">{formatRenewDate(stream)}</span>
        </div>
        {blocked ? (
          <div className="stream-card-meta-row">
            <span className="text-muted text-sm">Plan status</span>
            <span className="text-sm">{PLAN_STATE_LABEL[blocked]} — streaming blocked until renewed</span>
          </div>
        ) : null}
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">End Time</span>
          <span className="text-sm">{formatEndInfo(stream)}</span>
        </div>
      </div>
      <div className="stream-card-footer">
        <Button variant="primary" size="sm" onClick={() => onRenew(stream)}>Renew</Button>
        <button
          className="icon-button stream-card-delete"
          aria-label={`Delete ${stream.name}`}
          title="Delete stream"
          onClick={() => onDelete(stream)}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" /><path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * Renew popup — a two-step flow:
 *  1. Ask: renew the same plan, or change this stream to a different plan.
 *  2. Pay: the same plan checks out at the amount last paid for this stream; a
 *     plan change shows the plan picker and checks out at that plan's price.
 *
 * The renewal date is NOT advanced here. Razorpay only reports that payment was
 * submitted; the server-side webhook moves the date and switches the stream's
 * plan once capture is confirmed, so a dismissed or failed checkout cannot fake
 * a renewal.
 */
function RenewDialog({
  stream,
  onClose,
  onDone,
}: {
  stream: StreamDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const ui = useUiStore();
  // null while loading, so the picker never shows "no plans" before the fetch.
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  // null = the tenant has not answered yet; the default view is derived below.
  const [step, setStep] = useState<'choice' | 'plans' | null>(null);
  const [busyPlanId, setBusyPlanId] = useState<string | null>(null);

  const currentPlan = stream.plan ?? null;

  useEffect(() => {
    void http
      .get<PlanDto[]>('/api/plans')
      .then((all) => setPlans(all.filter((p) => p.status === 'ACTIVE')))
      .catch((e) => {
        setPlans([]);
        ui.toast('danger', 'Could not load plans', (e as Error).message);
      });
  }, [ui]);

  // The stream's plan is only renewable while it is still on the shelf.
  const samePlan = currentPlan && plans ? plans.find((p) => p.id === currentPlan.id) ?? null : null;
  // Nothing to re-buy (never renewed, or the plan was retired) -> the picker is
  // the only path, so skip the question.
  const view: 'choice' | 'plans' = step ?? (samePlan ? 'choice' : 'plans');
  const otherPlans = plans?.filter((p) => p.id !== currentPlan?.id) ?? null;

  // Same-plan renewals are charged at the amount last paid for this stream
  // (the server repeats this rule); fall back to the plan's own price when the
  // stream was never charged before.
  const cycle: BillingCycle = stream.billingCycle ?? 'MONTHLY';
  const sameAmount = samePlan
    ? stream.lastPaidAmount ?? priceForCycle(samePlan, cycle)
    : 0;
  const sameCurrency = stream.lastPaidCurrency ?? samePlan?.currency ?? 'INR';

  const pay = async (planId: string, planName: string, payCycle: BillingCycle) => {
    setBusyPlanId(planId);
    try {
      const order = await http.post<RazorpayOrderResponseDto>('/api/billing/orders', {
        planId,
        billingCycle: payCycle,
        streamId: stream.id,
      });
      const Razorpay = await loadCheckout();
      const handle = new Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'LiveStreamX',
        description: `${planName} — ${stream.name}`,
        order_id: order.orderId,
        handler: (res) => {
          ui.toast(
            'success',
            'Payment submitted',
            `Ref ${res.razorpay_payment_id} — the stream is updated once the server confirms the payment.`,
          );
          onDone();
        },
        modal: { ondismiss: () => setBusyPlanId(null) },
      });
      handle.open();
    } catch (e) {
      const msg = (e as Error).message ?? '';
      ui.toast('danger', msg.includes('not configured') ? 'Billing not configured' : 'Payment error', msg);
      setBusyPlanId(null);
    }
  };

  const money = (amount: number, currency: string) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);

  return (
    <Modal
      title={`Renew “${stream.name}”`}
      onClose={onClose}
      footer={
        view === 'choice' ? (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={busyPlanId !== null} onClick={() => setStep('plans')}>
                Change plan…
              </Button>
              {samePlan ? (
                <Button
                  variant="primary"
                  disabled={busyPlanId !== null}
                  onClick={() => void pay(samePlan.id, samePlan.name, cycle)}
                >
                  {busyPlanId ? 'Opening checkout…' : `Renew same plan · ${money(sameAmount, sameCurrency)}`}
                </Button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            {samePlan ? (
              <Button variant="ghost" onClick={() => setStep('choice')}>← Back</Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
            )}
            {samePlan ? <Button variant="ghost" onClick={onClose}>Cancel</Button> : null}
          </>
        )
      }
    >
      <div className="grid gap-2">
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">Renew date</span>
          <span className="text-sm">{formatRenewDate(stream)}</span>
        </div>
        <div className="stream-card-meta-row">
          <span className="text-muted text-sm">Current plan</span>
          <span className="text-sm">{currentPlan ? currentPlan.name : 'None'}</span>
        </div>

        {view === 'choice' && samePlan ? (
          <>
            <div className="stream-card-meta-row">
              <span className="text-muted text-sm">Last paid</span>
              <span className="text-sm">
                {money(sameAmount, sameCurrency)}
                {stream.billingCycle ? ` · ${stream.billingCycle.toLowerCase()}` : ''}
              </span>
            </div>
            <p className="text-sm text-muted" style={{ margin: 0 }}>
              Renew “{samePlan.name}” for {money(sameAmount, sameCurrency)}, or change this
              stream to a different plan. The new renew date is applied automatically once
              the payment is confirmed.
            </p>
          </>
        ) : null}

        {view === 'plans' ? (
          <>
            <p className="text-sm text-muted" style={{ margin: 0 }}>
              {currentPlan
                ? samePlan
                  ? 'Change this stream to another plan.'
                  : 'The plan this stream was on is no longer available — pick a current plan to continue.'
                : 'This stream has no plan yet; pick one to start its billing.'}{' '}
              The stream is updated with the new plan once the payment is confirmed.
            </p>
            {otherPlans === null ? <div className="text-muted">Loading plans…</div> : null}
            {otherPlans !== null && otherPlans.length === 0 ? (
              <div className="text-muted">
                {plans && plans.length > 0
                  ? 'This stream is already on the only available plan.'
                  : 'No active plans available.'}
              </div>
            ) : null}
            {otherPlans?.map((p) => (
              <div key={p.id} className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  {p.description ? <div className="text-sm text-muted">{p.description}</div> : null}
                </div>
                <Button
                  variant="secondary"
                  disabled={busyPlanId !== null}
                  onClick={() => void pay(p.id, p.name, 'MONTHLY')}
                >
                  {busyPlanId === p.id ? 'Opening checkout…' : `Pay ${money(p.priceMonth, p.currency)}`}
                </Button>
              </div>
            ))}
          </>
        ) : null}
      </div>
    </Modal>
  );
}

export function StreamsPage() {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Paginated<StreamDto> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [delTarget, setDelTarget] = useState<StreamDto | null>(null);
  const [renewTarget, setRenewTarget] = useState<StreamDto | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Subscription (plan strip) reloads independently: deleting a stream must
  // refresh "streams used" without also re-fetching the whole stream page.
  const [subReload, setSubReload] = useState(0);
  const [sub, setSub] = useState<SubView | null>(null);
  // Manual creation from the plan strip — non-null only while the modal is open.
  const [creatingFor, setCreatingFor] = useState<SubPlanRow | null>(null);
  const [streamName, setStreamName] = useState('');
  const [streamBusy, setStreamBusy] = useState(false);
  const ui = useUiStore();
  const branding = useBrandingStore();
  // Server-side enforced; mirror it here so the button is not a dead end.
  const hasPlan = useHasActivePlan();
  useNow(60_000);

  useEffect(() => {
    void http
      .get<Paginated<StreamDto>>('/api/tenant/streams', { page, pageSize: 12 })
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load streams'));
  }, [page, reloadKey]);

  useEffect(() => {
    void http.get<SubView | null>('/api/billing/subscription').then(setSub).catch(() => {});
  }, [reloadKey, subReload]);

  // Due date passed without renewal: no entitled plan left → broadcasting is
  // refused server-side (validateIngestKey) and the tenant sees this banner.
  const expired =
    !!sub &&
    sub.plans.length === 0 &&
    (sub.status !== 'ACTIVE' || (!!sub.endAt && new Date(sub.endAt).getTime() <= Date.now()));

  const waNumber = branding.supportWhatsapp?.trim() || null;
  const waDigits = waNumber?.replace(/\D/g, '') ?? '';
  const waHref = waDigits.length >= 7 ? `https://wa.me/${waDigits}` : null;
  // Only plans without a stream yet get a strip card — once a stream exists
  // on a plan, that plan is represented by its stream card below instead.
  const openPlans = sub?.plans.filter((p) => p.streamsUsed === 0) ?? [];

  const createStream = async () => {
    if (!creatingFor) return;
    setStreamBusy(true);
    try {
      const created = await http.post<{ rtmpUrl?: string }>('/api/tenant/streams', {
        name: streamName.trim(),
        planId: creatingFor.planId,
      });
      ui.toast('success', `Stream "${streamName.trim()}" created`, created.rtmpUrl);
      setCreatingFor(null);
      setStreamName('');
      // Refetch streams AND the plan strip (streamsUsed 0 → 1, button hides).
      setReloadKey((k) => k + 1);
    } catch (e) {
      ui.toast('danger', 'Create failed', (e as Error).message);
    } finally {
      setStreamBusy(false);
    }
  };

  const deleteStream = async () => {
    if (!delTarget) return;
    try {
      await http.delete(`/api/tenant/streams/${delTarget.id}`);
      ui.toast('success', `"${delTarget.name}" deleted`);
      setData((prev) => prev ? { ...prev, items: prev.items.filter((s) => s.id !== delTarget.id) } : prev);
      // The plan card ("No stream created…", streamsUsed) must reappear after
      // the last stream on a plan is deleted.
      setSubReload((k) => k + 1);
    } catch (e) {
      ui.toast('danger', 'Delete failed', (e as Error).message);
    } finally {
      setDelTarget(null);
    }
  };

  return (
    <AppLayout brand="LiveStreamX" navGroups={NAV}>
      <div className="flex justify-between items-center mb-2">
        <h1 className="page-title">Streams</h1>
      </div>
      {expired && sub ? (
        <Alert kind="danger">
          Your subscription {sub.endAt ? `ended on ${longDate(sub.endAt)}` : 'has expired'} —
          broadcasting is blocked until it is renewed. <Link to="/billing">Renew now</Link> or
          contact the administrator
          {waHref ? (
            <>
              {' — '}
              <a href={waHref} target="_blank" rel="noopener noreferrer">
                <WhatsAppIcon /> {waNumber}
              </a>
            </>
          ) : '.'}
        </Alert>
      ) : null}
      {hasPlan === false && !expired ? (
        <Alert kind="info">
          No active plan yet — creating a stream will ask you to pick and pay for a plan first.{' '}
          <Link to="/billing">Billing</Link>
        </Alert>
      ) : null}
      {/* Plan strip: mirrors the admin tenant page — cards only for entitled
          plans that hold no stream yet ("already created" plans show their
          stream card below). After a delete the card reappears; `endAt` comes
          from the subscription row, so a re-created stream inherits the same
          renew date. */}
      {openPlans.length > 0 ? (
        <div className="grid gap-2 mb-2">
          {openPlans.map((p) => (
            <div key={p.id} className="card">
              <div className="flex items-center gap-2">
                <span className="font-medium">{p.name}</span>
                <StatusBadge status={sub.status} />
              </div>
              <div className="text-sm text-muted">
                {sub.billingCycle ? `${sub.billingCycle.toLowerCase()} · ` : ''}
                {p.endAt ? `ends ${longDate(p.endAt)}` : 'no end date'}
              </div>
              <div
                className="text-sm text-muted"
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}
              >
                No stream created on this plan yet.
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => { setCreatingFor(p); setStreamName(p.name); }}
                >
                  Create stream…
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {error ? <EmptyState title="Failed to load streams" body={error} /> : null}
      {data && data.items.length === 0 && hasPlan !== false && openPlans.length === 0 ? (
        <EmptyState
          title="No streams yet"
          body="Create your first live stream and start broadcasting to your audience."
          action={<Link to="/streams/create"><Button>Create stream</Button></Link>}
        />
      ) : null}
      {data && data.items.length > 0 ? (
        <>
          <div className="stream-grid">
            {data.items.map((s) => (
              <StreamCard key={s.id} stream={s} onDelete={setDelTarget} onRenew={setRenewTarget} />
            ))}
          </div>
          <div className="mt-2">
            <Pagination page={data.page} totalPages={data.totalPages} onPage={setPage} />
          </div>
        </>
      ) : null}
      {creatingFor && (
        <Modal
          title={`Create stream for ${creatingFor.name}`}
          onClose={() => { if (!streamBusy) { setCreatingFor(null); setStreamName(''); } }}
          footer={
            <>
              <Button variant="secondary" disabled={streamBusy} onClick={() => { setCreatingFor(null); setStreamName(''); }}>Cancel</Button>
              <Button disabled={streamBusy || !streamName.trim()} onClick={() => void createStream()}>
                {streamBusy ? 'Creating…' : 'Create stream'}
              </Button>
            </>
          }
        >
          <p className="text-muted text-sm" style={{ marginTop: 0 }}>
            Creates the stream on this plan together with its RTMP ingest, HLS and player links.
            The renew date is inherited from the plan — it does not change.
          </p>
          <Input
            label="Stream name"
            value={streamName}
            onChange={setStreamName}
            placeholder="e.g. Main"
            hint="Becomes part of the ingest URL, e.g. rtmp://…/main/live — pick something stable."
          />
        </Modal>
      )}
      {delTarget && (
        <ConfirmDialog
          title="Delete stream?"
          body={`Delete "${delTarget.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          onConfirm={() => void deleteStream()}
          onCancel={() => setDelTarget(null)}
        />
      )}
      {renewTarget && (
        <RenewDialog
          stream={renewTarget}
          onClose={() => setRenewTarget(null)}
          onDone={() => {
            setRenewTarget(null);
            // The webhook confirms the capture server-side moments after the
            // checkout closes; poll a few times so the new renew date (and any
            // plan switch) appears without a manual refresh.
            for (const ms of [3000, 8000, 16000]) {
              window.setTimeout(() => setReloadKey((k) => k + 1), ms);
            }
          }}
        />
      )}
    </AppLayout>
  );
}